import styled from "@emotion/styled";
import { useEffect, useState, useRef, useCallback } from "react";
import { COLORS, calculateDays, priceFormatter, dateFormatter } from "../../settings/themeSettings";
import "./editableTable.scss";
import { Delete, ContentCopy } from "@mui/icons-material";
import { Button, Tooltip, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { frFR } from "@mui/x-date-pickers/locales";
import { Autocomplete, TextField } from "@mui/material";
import { useGridNavigation } from "../../hooks/useGridNavigation";

const Root = styled('div')(
  () => `
    position: relative;
    overflow-x: auto;
    overflow-y: auto;
    max-height: 480px;
    width: 100%;
    border: 1px solid ${COLORS.gray?.[200] ?? '#e0e0e0'};
    border-radius: 8px;
    background-color: #fff;

    table {
      font-family: 'Inter', 'Roboto', sans-serif;
      font-size: 0.75rem;
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      min-width: max-content;
      table-layout: fixed;
    }

    td, th {
      border: 1px solid ${COLORS.gray?.[200] ?? '#e0e0e0'};
      text-align: center !important;
      padding: 2px 3px;
      color: ${COLORS.secondary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      vertical-align: middle;
      height: 30px;
      line-height: 1.2;
    }

    /* Largeurs par défaut par type */
    .col-date   { width: 115px; min-width: 115px; }
    .col-number { width: 60px;  min-width: 50px; }
    .col-price  { width: 90px;  min-width: 70px; }
    .col-text   { width: 130px; min-width: 80px; }
    .col-actions{ width: 52px;  min-width: 52px; }

    /* En-têtes figées (Sticky Header) */
    thead th {
      background-color: ${COLORS.secondary};
      color: #fff;
      position: sticky;
      top: 0;
      z-index: 20;
      font-size: 0.70rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      text-align: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);

      span {
        display: block;
        text-align: center;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    /* Ligne active en surbrillance */
    tbody tr.row-active td {
      background-color: #edf3fc !important;
    }

    tbody tr.row-active td:first-of-type {
      border-left: 3px solid ${COLORS.primary} !important;
    }

    .total-header {
      background-color: #f8f9ff;
      font-weight: 800;
      color: ${COLORS.primary};
      font-size: 0.75rem;
      border-bottom: 2px solid ${COLORS.primary}40;
      text-align: center;
    }

    tfoot .total-header {
      background-color: ${COLORS.secondary}15;
      border-top: 2px solid ${COLORS.secondary}40;
      position: sticky;
      bottom: 0;
      z-index: 15;
    }

    input, select {
      width: 100%;
      padding: 2px 4px;
      border: 1px solid transparent;
      border-radius: 4px;
      background: transparent;
      font-family: inherit;
      font-size: 0.75rem;
      color: inherit;
      transition: all 0.15s;
      text-align: center;
      min-width: 60px;

      &:focus {
        outline: none;
        border-color: ${COLORS.primary};
        background: #fff;
        box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.25);
      }

      &[readonly] {
        color: ${COLORS.primary};
        font-weight: 600;
        cursor: default;
      }
    }

    .actions-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
    }

    select {
      min-width: 100px;
      cursor: pointer;
    }

    tr:nth-of-type(even) td {
      background-color: #fafbff;
    }

    tr:hover td {
      background-color: #f0f4ff;
      transition: background 0.1s;
    }
  `,
);

const nameFormatter = (name) => {
  return name
    .replace(/_/g, " ")
    .replace(/^frais$/i, "Frais")
    .replace(/montantEstime/g, "Montant estimé")
    .replace(/montantReel/g, "Montant réel")
    .replace(/numero/gi, "N°")
    .replace(/prixUnitaire/g, "PU")
    .replace(/lieuDepart/g, "Départ")
    .replace(/lieuArrivee/g, "Arrivée")
    .replace(/dateDebut/g, "Date début")
    .replace(/dateFin/g, "Date fin")
    .replace(/typeChambre/g, "Type ch.")
    .replace(/categorieChambre/g, "Catégorie")
    .replace(/nombreJour/g, "Jours")
    .replace(/depouillement/g, "Dépouillement")
    .replace(/montant/gi, "Montant")
    .replace(/ecart/gi, "Écart")
    .replaceAll(/([A-Z])/g, ' $1')
    .trim();
};

const calculateProduct = (labels, row) => {
  const p = labels
    .map((label) => row[label])
    .reduce((acc, cur) => ({ product: acc.product * (Number(cur) || 0) }), { product: 1 }).product;
  return isNaN(p) ? 0 : Math.round(p * 100) / 100;
};

const evaluateOperationsRow = (row, operations, changedProp = null) => {
  if (Object.keys(operations).length === 0) return row;
  let newRow = { ...row };
  
  const dirty = changedProp ? [changedProp] : Object.keys(row);
  
  Object.entries(operations).forEach(([opProp, op]) => {
    if (opProp === changedProp) return;

    const shouldRecalculate = op.labels.some(label => dirty.includes(label));
    
    if (shouldRecalculate) {
      if (op.type === '*') newRow[opProp] = calculateProduct(op.labels, newRow);
      if (op.type === 'daydiff') newRow[opProp] = calculateDays(newRow[op.labels[0]], newRow[op.labels[1]]);
      if (op.type === 'daydiff+') newRow[opProp] = calculateDays(newRow[op.labels[0]], newRow[op.labels[1]]) + 1;
      if (op.type === 'diff') newRow[opProp] = (Number(newRow[op.labels[0]]) || 0) - (Number(newRow[op.labels[1]]) || 0);
      
      dirty.push(opProp);
    }
  });

  return newRow;
};

const EditableTable = ({ 
  reference = {}, 
  choices = {}, 
  data = [], 
  operations = {}, 
  readOnlyProps = [], 
  showButton = true, 
  onUpdate, 
  onChoiceChange,
  onReachEnd = null,
  maxHeight = '480px'
}) => {
  const [rows, setRows] = useState(data.map(row => evaluateOperationsRow(row, operations)));
  const [lastAddedRowIdx, setLastAddedRowIdx] = useState(-1);
  const containerRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Synchronisation initiale et quand data change de l'extérieur
  useEffect(() => {
    const nextRows = data.map(row => evaluateOperationsRow(row, operations));
    if (JSON.stringify(nextRows) !== JSON.stringify(rows)) {
      setRows(nextRows);
    }
  }, [data, operations]);

  const colKeys = Object.keys(reference);

  const addReference = useCallback((template = null) => {
    let newRow;
    
    if (template) {
      newRow = { ...template };
    } else if (rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      newRow = { ...reference };
      
      if ('pax' in lastRow) newRow.pax = lastRow.pax;
      if ('regime' in lastRow) newRow.regime = lastRow.regime;
      if ('typeChambre' in lastRow) newRow.typeChambre = lastRow.typeChambre;
      if ('categorieChambre' in lastRow) newRow.categorieChambre = lastRow.categorieChambre;
      if ('lieuArrivee' in lastRow && 'lieuDepart' in newRow) newRow.lieuDepart = lastRow.lieuArrivee;

      if ('dateFin' in lastRow && 'dateDebut' in newRow) {
        newRow.dateDebut = lastRow.dateFin;
      }
    } else {
      newRow = { ...reference };
    }

    const _rows = [...rows, evaluateOperationsRow(newRow, operations)];
    setRows(_rows);
    setLastAddedRowIdx(_rows.length - 1);
    onUpdateRef.current(_rows);
  }, [rows, reference, operations]);

  // Hook de navigation clavier conforme à la Note Technique AFT
  const {
    activeRowIdx,
    setActiveRowIdx,
    setActiveCoords,
    handleKeyDown
  } = useGridNavigation({
    rowCount: rows.length,
    colCount: colKeys.length,
    onAddRow: addReference,
    onReachEnd,
    containerRef
  });

  const duplicateRow = (idx) => {
    const rowToClone = { ...rows[idx] };
    const _rows = [...rows];
    _rows.splice(idx + 1, 0, evaluateOperationsRow(rowToClone, operations));
    setRows(_rows);
    onUpdateRef.current(_rows);
  };

  const getChoices = (prop) => {
    return choices[prop].options
      ? [choices[prop].options, choices[prop].optionValue, choices[prop].optionLabel]
      : [choices[prop]];
  };

  const hasChoices = (prop) => Object.prototype.hasOwnProperty.call(choices, prop);
  const hasOperations = (prop) => Object.prototype.hasOwnProperty.call(operations, prop);

  const deleteLine = (idx) => {
    const nextRows = rows.filter((_, i) => i !== idx);
    setRows(nextRows);
    onUpdateRef.current(nextRows);
  };

  const changeProp = (value, property, itemIdx) => {
    const rowUpdate = rows.map((row, idx) => {
      if (itemIdx === idx) {
        return evaluateOperationsRow({ ...row, [property]: value }, operations, property);
      }
      return row;
    });
    setRows(rowUpdate);
    onUpdateRef.current(rowUpdate);
  };

  const calculateTotal = (prop) =>
    rows.reduce((sum, row) => sum + (Number(row[prop]) || 0), 0);

  const isNumeric = (prop) =>
    /montant|prix|tarif|depouillement|marge|pax|nuitee|nombre|jour|debours|euros|cfa|guide/i.test(prop);

  const shouldSum = (prop) => 
    isNumeric(prop) && !/prixUnitaire|pu|tarif/i.test(prop);

  const getColumnClass = (prop) => {
    if (/date/i.test(prop)) return 'col-date';
    if (/montant|prix|tarif/i.test(prop)) return 'col-price';
    if (/pax|nuitee|nombre|jour/i.test(prop)) return 'col-number';
    return 'col-text';
  };

  return (
    <Root className="editable" ref={containerRef} style={{ maxHeight }}>
      <table aria-label="editable table">
        <thead>
          {/* EN-TÊTES DES COLONNES FIGÉES */}
          <tr>
            {colKeys.map((prop, idx) => (
              <th key={idx} className={getColumnClass(prop)}>
                <Tooltip title={prop} arrow>
                  <span>{nameFormatter(prop)}</span>
                </Tooltip>
              </th>
            ))}
            <th className="col-actions"><Delete fontSize="small" /></th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => (
            <tr 
              key={idx} 
              className={activeRowIdx === idx ? 'row-active' : ''}
              onClick={() => {
                setActiveRowIdx(idx);
                setActiveCoords(prev => ({ ...prev, row: idx }));
              }}
            >
              {Object.entries(row).map(([prop, value], colIdx) => (
                <td 
                  key={colIdx} 
                  className={getColumnClass(prop)}
                  data-row-idx={idx}
                  data-col-idx={colIdx}
                >
                  {hasChoices(prop) ? (
                    <Autocomplete
                      size="small"
                      openOnFocus
                      options={getChoices(prop)[0]}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        if (option.isAction) return option.libelle || option.nomComplet || option.matricule || option.nom || '';
                        return option[getChoices(prop)[2]] || '';
                      }}
                      filterOptions={(options, { inputValue }) => {
                        const filtered = options.filter(o => 
                          !o.isAction &&
                          (o[getChoices(prop)[2]] || '').toLowerCase().includes(inputValue.toLowerCase())
                        );
                        
                        if (inputValue !== '' && onChoiceChange) {
                          const isExisting = options.some(o => 
                            !o.isAction &&
                            inputValue.toLowerCase() === (o[getChoices(prop)[2]] || '').toLowerCase()
                          );
                          if (!isExisting) {
                            const actionOption = options.find(o => o.isAction);
                            if (actionOption) {
                              filtered.push({
                                ...actionOption,
                                inputValue: inputValue,
                                [getChoices(prop)[2]]: `Créer "${inputValue}"...`
                              });
                            }
                          }
                        }

                        if (inputValue === '' && onChoiceChange) {
                          const actionOption = options.find(o => o.isAction);
                          if (actionOption) filtered.push(actionOption);
                        }

                        return filtered;
                      }}
                      value={(() => {
                        if (value === '' || value === null || value === undefined || value === 0) return null;
                        return getChoices(prop)[0].find(choice => {
                          if (choice.isAction) return false;
                          const choiceValue = getChoices(prop)[1] ? choice[getChoices(prop)[1]] : choice;
                          return String(choiceValue) === String(value);
                        }) || null;
                      })()}
                      onChange={(event, newValue) => {
                        if (!newValue) {
                          changeProp('', prop, idx);
                          return;
                        }
                        if (newValue.isAction && onChoiceChange) {
                          setTimeout(() => onChoiceChange(prop, newValue, idx), 0);
                          return;
                        }
                        const val = getChoices(prop)[1] ? newValue[getChoices(prop)[1]] : newValue;
                        changeProp(val, prop, idx);
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          variant="standard" 
                          placeholder="..."
                          autoFocus={lastAddedRowIdx === idx && colIdx === 0}
                          onFocus={() => {
                            setActiveRowIdx(idx);
                            setActiveCoords({ row: idx, col: colIdx });
                          }}
                          onKeyDown={(e) => handleKeyDown(e, idx, colIdx)}
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            style: { fontSize: '0.75rem', textAlign: 'center' }
                          }}
                          inputProps={{
                            ...params.inputProps,
                            'data-row-idx': idx,
                            'data-col-idx': colIdx,
                            style: { ...params.inputProps.style, textAlign: 'center', padding: '2px' }
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-endAdornment': { display: 'none' },
                        '& .MuiInputBase-root': { padding: '0 !important' }
                      }}
                      componentsProps={{
                        popper: {
                          style: { width: 'auto', minWidth: 220 },
                          placement: 'bottom-start',
                          modifiers: [{ name: 'flip', enabled: true }]
                        }
                      }}
                    />
                  ) : hasOperations(prop) ? (() => {
                    const op = operations[prop];
                    let computed = row[prop];
                    if (computed === undefined || computed === null) {
                      if (op.type === '*') computed = calculateProduct(op.labels, row);
                      if (op.type === 'daydiff') computed = calculateDays(row[op.labels[0]], row[op.labels[1]]);
                      if (op.type === 'daydiff+') computed = calculateDays(row[op.labels[0]], row[op.labels[1]]) + 1;
                      if (op.type === 'diff') computed = (Number(row[op.labels[0]]) || 0) - (Number(row[op.labels[1]]) || 0);
                    }
                    const displayVal = /date/.test(prop) ? dateFormatter(computed) : (isNumeric(prop) ? priceFormatter(computed) : computed ?? 0);
                    return (
                      <Tooltip title={String(displayVal)} arrow>
                        <input
                          type="text"
                          value={displayVal}
                          readOnly={readOnlyProps.includes(prop)}
                          data-row-idx={idx}
                          data-col-idx={colIdx}
                          onFocus={() => {
                            setActiveRowIdx(idx);
                            setActiveCoords({ row: idx, col: colIdx });
                          }}
                          onChange={(e) => changeProp(e.target.value, prop, idx)}
                          onKeyDown={(e) => handleKeyDown(e, idx, colIdx)}
                        />
                      </Tooltip>
                    );
                  })() : (
                    <Tooltip title={String(value || "")} arrow disableHoverListener={!value}>
                      {/date/i.test(prop) ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs} localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
                          <DatePicker
                            value={value && dayjs(value).isValid() ? dayjs(value) : null}
                            onChange={(newVal) => changeProp(newVal ? newVal.format('YYYY-MM-DD') : '', prop, idx)}
                            format="DD/MM/YYYY"
                            slotProps={{
                              textField: {
                                size: 'small',
                                variant: 'standard',
                                onFocus: () => {
                                  setActiveRowIdx(idx);
                                  setActiveCoords({ row: idx, col: colIdx });
                                },
                                onKeyDown: (e) => handleKeyDown(e, idx, colIdx),
                                inputProps: { 
                                  'data-row-idx': idx,
                                  'data-col-idx': colIdx,
                                  style: { fontSize: '0.75rem', textAlign: 'center', padding: '2px 4px' } 
                                },
                                sx: { width: '110px' }
                              },
                              openPickerButton: { size: 'small', sx: { padding: '2px' } }
                            }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <input
                          type={isNumeric(prop) ? 'number' : 'text'}
                          value={value ?? ""}
                          data-row-idx={idx}
                          data-col-idx={colIdx}
                          autoFocus={lastAddedRowIdx === idx && colIdx === 0}
                          readOnly={readOnlyProps.includes(prop)}
                          onFocus={() => {
                            setActiveRowIdx(idx);
                            setActiveCoords({ row: idx, col: colIdx });
                          }}
                          onChange={(e) => changeProp(e.target.value, prop, idx)}
                          onKeyDown={(e) => handleKeyDown(e, idx, colIdx)}
                          placeholder={nameFormatter(prop)}
                        />
                      )}
                    </Tooltip>
                  )}
                </td>
              ))}
              <td className="actions-cell">
                <Tooltip title="Dupliquer la ligne">
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={() => duplicateRow(idx)} 
                      sx={{ color: COLORS.primary }}
                    >
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Supprimer la ligne">
                  <IconButton size="small" onClick={() => deleteLine(idx)} sx={{ color: COLORS.error ?? 'red' }}>
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          ))}

          {rows.length === 0 && (
            <tr>
              <td
                colSpan={colKeys.length + 1}
                style={{ textAlign: 'center', color: '#aaa', padding: '16px', fontStyle: 'italic' }}
              >
                Appuyez sur Entrée ou cliquez « + Ajouter une ligne » pour commencer
              </td>
            </tr>
          )}
        </tbody>

        {/* TOTAUX — EN PIED DE TABLEAU FIGÉ */}
        <tfoot>
          <tr>
            {colKeys.map((prop, idx) => {
              const total = calculateTotal(prop);
              const showTotal = shouldSum(prop) && total > 0;
              return (
                <th key={`total-${idx}`} className={`total-header ${getColumnClass(prop)}`}>
                  {showTotal
                    ? (prop.toLowerCase().includes('montant') || prop.toLowerCase().includes('cfa') || prop.toLowerCase().includes('euros') || prop.toLowerCase().includes('debours')
                        ? priceFormatter(total)
                        : total)
                    : (idx === 0 ? 'TOTAL' : '')}
                </th>
              );
            })}
            <th className="total-header col-actions">∑</th>
          </tr>
        </tfoot>
      </table>

      {showButton && (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          sx={{ mt: '6px', width: '100%', textTransform: 'none', fontSize: '0.78rem', borderStyle: 'dashed' }}
          onClick={() => addReference()}
        >
          + Ajouter une ligne &nbsp;<span style={{ opacity: 0.5, fontSize: '0.7rem' }}>(ou ↵ Entrée)</span>
        </Button>
      )}
    </Root>
  );
};

export default EditableTable;
